/* Wallpaper sample add-to-cart.
   While the "Sample" purchase option is selected, the add-to-cart form must
   submit the product's own sample variant (the variant whose first option
   value contains "sample", rendered as
   data-sample-variant-id on the picker). When the
   customer switches back to the full wallpaper option, the form reverts to
   the variant id it held before the sample was selected.

   This runs on top of the theme's own variant handling
   (instant-update-product-info), which only rewrites the form's variant id
   when the full option combination matches a real variant — selecting
   "Sample" usually has no match, so without this the form silently keeps the
   previous Full Mural variant. */
(() => {
  const getPickers = () => document.querySelectorAll('variant-selects.wp-picker');
  const PRICE_SELECTOR = '.price__container .price__regular .price-item--regular';

  // Diagnostics: open the product page with ?wp_sample_debug in the URL to
  // trace every sync step in the console.
  const DEBUG = window.location.search.includes('wp_sample_debug');
  const debug = (...args) => {
    if (DEBUG) console.log('[wp-sample]', ...args);
  };

  // Write both the property (what FormData submits) and the attribute (what
  // devtools' Elements panel shows) so the swap is observable.
  const setVariantId = (input, value) => {
    input.value = value;
    input.setAttribute('value', value);
  };

  // Resolve the currently checked option values to a variant of the product
  // itself, using the JSON map rendered inside the picker. Returns null when
  // the combination doesn't exist (e.g. "Sample" + a size).
  const resolveSelectedVariant = (picker) => {
    if (!picker.wpVariants) {
      const dataElement = picker.querySelector('script[data-wp-product-variants]');
      if (!dataElement) return null;
      try {
        picker.wpVariants = JSON.parse(dataElement.textContent);
      } catch (error) {
        picker.wpVariants = [];
      }
    }
    const selected = Array.from(picker.querySelectorAll('fieldset input:checked')).map((input) => input.value);
    if (!selected.length) return null;
    const matched =
      picker.wpVariants.find(
        (variant) =>
          variant.options.length === selected.length &&
          variant.options.every((option, index) => option === selected[index])
      ) || null;
    debug('resolve: selected =', selected, '-> matched variant =', matched && matched.id);
    return matched;
  };

  const syncSampleState = () => {
    const pickers = getPickers();
    debug('sync: pickers found =', pickers.length);
    pickers.forEach((picker) => {
      const sampleVariantId = picker.dataset.sampleVariantId;
      if (!sampleVariantId) {
        debug('sync: bail — no data-sample-variant-id on picker', picker);
        return;
      }

      const form = document.getElementById(`product-form-${picker.dataset.section}`);
      const idInput = form?.querySelector('input[name="id"]');
      if (!idInput) {
        debug(`sync: bail — form/id input not found (looked for #product-form-${picker.dataset.section})`, form);
        return;
      }

      const sampleInput = picker.querySelector('input.wp-option--sample');
      const sampleSelected = !!sampleInput?.checked;
      const priceElement = document.querySelector(PRICE_SELECTOR);
      debug('sync: sampleVariantId =', sampleVariantId, '| sampleInput found =', !!sampleInput, '| sampleSelected =', sampleSelected, '| id before =', idInput.value);

      if (sampleSelected) {
        if (idInput.value !== sampleVariantId) {
          // Remember the wallpaper variant so we can revert to it later.
          form.dataset.wpOriginalVariantId = idInput.value;
          if (priceElement) form.dataset.wpOriginalPrice = priceElement.textContent;
          // Set the value directly (no change event) to avoid re-trigger loops.
          setVariantId(idInput, sampleVariantId);
          debug('sync: id input set to sample variant', sampleVariantId);
        }
        idInput.removeAttribute('disabled');

        const submitButton = form.querySelector('.product-form__submit');
        if (submitButton) {
          submitButton.removeAttribute('disabled');
          submitButton.setAttribute('aria-disabled', 'false');
        }

        // Keep the displayed price in sync with what will be added.
        const samplePrice = picker.dataset.sampleVariantPrice;
        if (samplePrice && priceElement && priceElement.textContent.trim() !== samplePrice) {
          priceElement.textContent = samplePrice;
        }
      } else {
        // Full wallpaper flow: the form id must follow the checked options.
        const matched = resolveSelectedVariant(picker);
        if (matched) {
          if (idInput.value !== String(matched.id)) {
            setVariantId(idInput, matched.id);
            debug('sync: id input set to matched full variant', matched.id);
          }
          delete form.dataset.wpOriginalVariantId;
          if (form.dataset.wpOriginalPrice) {
            // Restore the pre-sample price only if the sample price is still
            // showing (another script may already have written the new one).
            const samplePrice = picker.dataset.sampleVariantPrice;
            if (priceElement && (!samplePrice || priceElement.textContent.trim() === samplePrice.trim())) {
              priceElement.textContent = form.dataset.wpOriginalPrice;
            }
            delete form.dataset.wpOriginalPrice;
          }
        } else if (idInput.value === sampleVariantId) {
          // No matching combination but the form still points at the sample:
          // revert to the variant id it held before the sample was selected.
          if (form.dataset.wpOriginalVariantId) {
            setVariantId(idInput, form.dataset.wpOriginalVariantId);
            delete form.dataset.wpOriginalVariantId;
          }
          if (priceElement && form.dataset.wpOriginalPrice) {
            priceElement.textContent = form.dataset.wpOriginalPrice;
            delete form.dataset.wpOriginalPrice;
          }
        } else {
          debug('sync: no matching variant for current selection; id left as', idInput.value);
        }
      }
    });
  };

  // Fallback when the picker was rendered without a sample variant id:
  // resolve it from the current product's own sample variant.
  const ensureSampleVariantId = () => {
    getPickers().forEach((picker) => {
      if (picker.dataset.sampleVariantId || picker.dataset.sampleVariantPending) return;
      picker.dataset.sampleVariantPending = 'true';
      fetch(`${window.location.pathname}.json`)
        .then((response) => response.json())
        .then(({ product }) => {
          const variants = product?.variants || [];
          const isSample = (variant) => (variant.option1 || '').toLowerCase().includes('sample');
          const sampleVariant = variants.find((v) => isSample(v) && v.available) || variants.find(isSample);
          if (sampleVariant) {
            picker.dataset.sampleVariantId = sampleVariant.id;
            syncSampleState();
          }
        })
        .catch(() => {});
    });
  };

  // Runs when a picker radio changes. Document-level listeners fire after
  // variant-selects' own change listener, so this always runs after the
  // theme's synchronous pub/sub subscribers (instant-update-product-info).
  document.addEventListener('change', (event) => {
    if (event.target.closest?.('variant-selects.wp-picker') || event.target.matches?.('input[name="id"]')) {
      debug('change event from', event.target);
      syncSampleState();
    }
  });

  // Re-apply after any of the theme's variant events settle, in case another
  // subscriber rewrote the variant id input without dispatching an event.
  const subscribeToThemeEvents = () => {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS !== 'object') return;
    ['optionValueSelectionChange', 'variantSelectedChanged', 'variantChange', 'variantOptionsSortingChanged'].forEach(
      (eventName) => {
        if (PUB_SUB_EVENTS[eventName]) {
          subscribe(PUB_SUB_EVENTS[eventName], () => setTimeout(syncSampleState, 0));
        }
      }
    );
  };

  // Safety net: correct the id at the moment of submit, before the
  // product-form component reads the form data (capture phase runs first).
  document.addEventListener(
    'submit',
    (event) => {
      if (event.target.matches?.('form[data-type="add-to-cart-form"]')) syncSampleState();
    },
    true
  );

  const init = () => {
    debug('init: script loaded, pickers on page =', getPickers().length);
    subscribeToThemeEvents();
    ensureSampleVariantId();
    syncSampleState();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
